import { ReactNode, useCallback, useRef } from 'react'
import {
  ReactZoomPanPinchContentRef,
  ReactZoomPanPinchContextState,
  TransformComponent,
  TransformWrapper,
} from 'react-zoom-pan-pinch'

import { ZOOM_LEVELS } from '../../constants/zoomLevels'
import { AspectRatio, Orientation } from '../../types'
import { checkBounds } from '../../utils/checkBounds'
import { getZoomableContainerWidth } from '../../utils/getZoomableContainerWidth'

/* Library height/width wrappers rewrites */
const ZoomWrappersStyles = { width: '100%', height: '100%', display: 'flex', alignItems: 'center' }

interface ZoomableWrapperProps {
  actionsComponent: ReactNode
  aspectRatio?: AspectRatio
  children: ReactNode
  fitToWidth?: boolean
  initialScale?: (typeof ZOOM_LEVELS)[number]
  isLoading: boolean
  orientation: Orientation
  zoomable: boolean
}

export const ZoomableWrapper = ({
  actionsComponent,
  aspectRatio,
  children,
  fitToWidth,
  initialScale,
  isLoading,
  orientation,
  zoomable,
}: ZoomableWrapperProps) => {
  const ref = useRef<ReactZoomPanPinchContentRef>(null)

  const enforceBounds = useCallback(
    (state: ReactZoomPanPinchContextState) => {
      if (!ref) {
        return
      }

      const {
        instance: { transformState },
      } = state

      const { isOutsideBounds, x, y, scale } = checkBounds(transformState)

      if (isOutsideBounds) {
        ref?.current?.setTransform(x, y, scale)
      }
    },
    [ref],
  )

  const ZoomContentStyles = {
    aspectRatio: (isLoading && aspectRatio) || 'auto',
    height: orientation === 'portrait' || isLoading ? '100%' : 'auto',
    maxHeight: '100%',
    margin: '0px auto',
    width: getZoomableContainerWidth({ fitToWidth, isLoading, orientation }),
  }

  if (!zoomable) {
    return children
  }

  return (
    <TransformWrapper
      centerOnInit={!!initialScale}
      disabled={!zoomable}
      doubleClick={{
        mode: 'reset',
      }}
      initialScale={initialScale}
      maxScale={4.0}
      minScale={0.5}
      limitToBounds={false}
      onPanningStop={enforceBounds}
      ref={ref}
    >
      <TransformComponent contentStyle={ZoomContentStyles} wrapperStyle={ZoomWrappersStyles}>
        {children}
      </TransformComponent>
      {actionsComponent}
    </TransformWrapper>
  )
}
